import type { FloorPlan3DMaterialSlot } from "../../../materials/materialResolver";
import type { PrimitiveSymbol3DModelKey } from "../../registry/primitiveSymbol3DConfig";
import type { LivingRoom3DPart } from "./livingRoomGeometry";

const HANGER_COUNTS: Partial<Record<PrimitiveSymbol3DModelKey, number>> = {
  "symbols:hangers-1": 5,
  "symbols:hangers-2": 7,
  "symbols:hangers-3": 9,
  "symbols:hangers-4": 11,
};

export function createHangersParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const railRadius = Math.min(width * 0.022, depth * 0.03, height * 0.012);
  const railLength = width;
  const hangerRadius = Math.min(depth * 0.011, height * 0.004, 0.0065);
  const hookRadius = hangerRadius * 0.78;
  const hookOuterRadius = railRadius + hookRadius * 1.6;
  const railZ = baseElevation + height - hookOuterRadius - hookRadius;
  const hangerDepth = depth * 0.76;
  const shoulderDrop = Math.min(height * 0.1, hangerDepth * 0.38);
  const neckDrop = Math.min(height * 0.055, depth * 0.14);
  const hangerCount = HANGER_COUNTS[modelKey] ?? 5;
  const hangerSpan = railLength - Math.max(width * 0.16, railRadius * 6);
  const startX = -hangerSpan / 2;
  const step = hangerCount > 1 ? hangerSpan / (hangerCount - 1) : 0;
  const parts: LivingRoom3DPart[] = [
    roundRodBetween(
      "rail",
      [-railLength / 2, 0, railZ],
      [railLength / 2, 0, railZ],
      railRadius,
      "furnitureMetal",
      24,
    ),
  ];

  for (let index = 0; index < hangerCount; index += 1) {
    const hangerX = hangerCount > 1 ? startX + index * step : 0;
    const yaw = ((index % 5) - 2) * 0.025;
    const apexZ = railZ - neckDrop;
    const barZ = apexZ - shoulderDrop;
    const halfDepth = hangerDepth / 2;
    const point = (localY: number, z: number): [number, number, number] => {
      const sin = Math.sin(yaw);
      const cos = Math.cos(yaw);
      return [hangerX - localY * sin, localY * cos, z];
    };
    const apex = point(0, apexZ);
    const frontTip = point(-halfDepth, barZ);
    const backTip = point(halfDepth, barZ);
    const hookPoints = [
      point(0, railZ - hookOuterRadius),
      point(-hookOuterRadius * 0.82, railZ - hookOuterRadius * 0.38),
      point(-hookOuterRadius, railZ + hookOuterRadius * 0.35),
      point(-hookOuterRadius * 0.35, railZ + hookOuterRadius),
      point(hookOuterRadius * 0.55, railZ + hookOuterRadius * 0.72),
    ];

    parts.push(
      roundRodBetween(
        `hanger-${index}-front-shoulder`,
        apex,
        frontTip,
        hangerRadius,
        "furnitureWood",
      ),
      roundRodBetween(
        `hanger-${index}-back-shoulder`,
        apex,
        backTip,
        hangerRadius,
        "furnitureWood",
      ),
      roundRodBetween(
        `hanger-${index}-crossbar`,
        frontTip,
        backTip,
        hangerRadius,
        "furnitureWood",
      ),
      roundRodBetween(
        `hanger-${index}-hook-stem`,
        apex,
        hookPoints[0],
        hookRadius,
        "furnitureMetal",
      ),
    );

    for (let hookIndex = 0; hookIndex < hookPoints.length - 1; hookIndex += 1) {
      parts.push(
        roundRodBetween(
          `hanger-${index}-hook-${hookIndex}`,
          hookPoints[hookIndex],
          hookPoints[hookIndex + 1],
          hookRadius,
          "furnitureMetal",
        ),
      );
    }
  }

  return parts;
}

function roundRodBetween(
  id: string,
  from: [number, number, number],
  to: [number, number, number],
  radius: number,
  materialSlot: FloorPlan3DMaterialSlot,
  radialSegments = 10,
): LivingRoom3DPart {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const dz = to[2] - from[2];
  const height = Math.hypot(dx, dy, dz);
  const horizontalLength = Math.hypot(dx, dz);

  // The renderer's base rotation makes cylinder height vertical. Cancel it,
  // then aim the cylinder's local height axis from `from` to `to`.
  return {
    id,
    type: "cylinder",
    center: [
      (from[0] + to[0]) / 2,
      (from[1] + to[1]) / 2,
      (from[2] + to[2]) / 2,
    ],
    height,
    radiusX: radius,
    radiusY: radius,
    radialSegments,
    materialSlot,
    useFillColor: false,
    rotationX: -Math.PI / 2,
    rotationY: horizontalLength > Number.EPSILON ? Math.atan2(dz, -dx) : 0,
    rotationZ: Math.atan2(horizontalLength, dy),
  };
}
